import { useEffect, useCallback, useState } from 'react';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from 'hooks/use-personal-information.hook';
import { useErrorHandler } from 'hooks/use-error-handler.hook';
import { CoverageExperience } from 'common/constants';
import {
  sendOnboardingScreenLoadedEvent,
  sendOnboardingButtonClickedEvent,
} from 'utils/analytics-events';

interface UseGetStartedProps {
  handleOnboardingCompleted: () => void;
  setHasCompletedGetStarted?: (completed: boolean) => void;
}

interface UseGetStartedReturn {
  // Data
  userData: any;

  // State
  isLoading: boolean;

  // Computed values
  coverageExperience: CoverageExperience;
  descriptionMessageId: string;
  screenName: string;

  // Actions
  handleClick: () => Promise<void>;

  // UI helpers
  formatMessage: any;
}

export const useGetStarted = ({
  handleOnboardingCompleted,
  setHasCompletedGetStarted,
}: UseGetStartedProps): UseGetStartedReturn => {
  const { data: userData } = usePersonalInformation();
  const { updateUserProfile, isLoading: isUpdating } = useUpdateUserProfile({});
  const { handleAccountSetupError } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(false);

  const coverageExperience =
    (userData?.customFields?.find(
      (field) => field.name === 'coverage_experience',
    )?.value as CoverageExperience) ?? CoverageExperience.Active;

  const descriptionMessageId =
    coverageExperience === CoverageExperience.PreEffective
      ? 'STR_GET_STARTED_DESC_PRE_EFFECTIVE'
      : 'STR_GET_STARTED_DESC_ACTIVE';

  const screenName =
    coverageExperience === CoverageExperience.PreEffective
      ? 'pre-effective members start'
      : 'effective members start';

  // Track screen loaded event
  useEffect(() => {
    sendOnboardingScreenLoadedEvent(screenName);
  }, [screenName]);

  const handleClick = useCallback(async () => {
    // Prevent multiple clicks while loading
    if (isLoading || isUpdating) {
      return;
    }

    // Set loading state immediately for user feedback
    setIsLoading(true);

    // Track let's go button click
    sendOnboardingButtonClickedEvent(screenName, "let's go");

    try {
      // Update get_started_completed flag
      await updateUserProfile({
        custom_fields: [
          ...(userData?.customFields || []).filter(
            (field) => field.name !== 'get_started_completed',
          ),
          { name: 'get_started_completed', value: 'true' },
        ],
      });

      // Mark as completed locally only after API success
      setHasCompletedGetStarted?.(true);
      handleOnboardingCompleted();
    } catch (error: any) {
      handleAccountSetupError(error);
      // Reset loading state on error so user can retry
      setIsLoading(false);
    }
  }, [
    screenName,
    updateUserProfile,
    userData?.customFields,
    handleOnboardingCompleted,
    handleAccountSetupError,
    setHasCompletedGetStarted,
    isLoading,
    isUpdating,
  ]);

  return {
    // Data
    userData,

    // State
    isLoading: isLoading || isUpdating,

    // Computed values
    coverageExperience,
    descriptionMessageId,
    screenName,

    // Actions
    handleClick,

    // UI helpers (this would come from useIntl in the component)
    formatMessage: null, // Will be provided by the component
  };
};
